import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Building2, Save, Loader2, ArrowRight } from "lucide-react";

const CATEGORIES = [
  "Retail", "Restaurant & Food", "E-commerce", "Healthcare",
  "Real Estate", "Education", "Beauty & Wellness", "Travel & Hospitality",
  "Fitness", "Professional Services", "B2B Services", "Manufacturing",
  "Fashion", "Technology", "Finance", "Non-profit", "Other",
];

export default function ProfileExtended() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    business_name: "", contact_person: "", phone: "", email: "",
    address: "", city: "", state: "", pin_code: "",
    country: "India", business_category: "Retail",
    gst_number: "", pan_number: "", website: "", special_instructions: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/onboarding/profile").then((r) => {
      if (r.data.profile) setForm((p) => ({ ...p, ...r.data.profile }));
    });
    // pre-fill name + email from user
    api.get("/auth/me").then((r) => {
      setForm((p) => ({
        ...p,
        contact_person: p.contact_person || r.data.name || "",
        email: p.email || r.data.email || "",
        phone: p.phone || r.data.phone || "",
      }));
    });
  }, []);

  const submit = async () => {
    const required = ["business_name", "contact_person", "phone", "email",
                       "address", "city", "state", "pin_code", "country", "business_category"];
    const missing = required.filter((k) => !form[k] || !String(form[k]).trim());
    if (missing.length) { toast.error(`Required: ${missing.join(", ")}`); return; }
    setSaving(true);
    try {
      await api.post("/onboarding/profile", form);
      toast.success("Profile saved. Next: choose your plan.");
      nav("/onboarding/plan");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const fld = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const inp = "w-full bg-white/5 border border-white/10 focus:border-indigo-500/60 outline-none rounded-lg px-3 py-2.5 text-sm";

  return (
    <div className="min-h-screen bg-[#05050A] text-white grid-bg p-6 lg:p-12">
      <div className="max-w-4xl mx-auto">
        <header className="mb-8">
          <div className="text-xs uppercase tracking-[0.25em] text-indigo-400 mb-2 flex items-center gap-2">
            <Building2 className="h-3.5 w-3.5" /> Step 2 of 3 · Business profile
          </div>
          <h1 className="font-heading text-3xl lg:text-5xl font-black tracking-tight">Tell us about your business</h1>
          <p className="text-white/50 mt-2">This helps our AI write in your voice and reach your audience.</p>
        </header>

        <div className="glass rounded-2xl p-6 lg:p-8 space-y-5" data-testid="profile-form">
          <Group title="Business identity">
            <div className="grid md:grid-cols-2 gap-4">
              <Lab label="Business name *"><input className={inp} value={form.business_name} onChange={fld("business_name")} data-testid="profile-business-name" /></Lab>
              <Lab label="Business category *">
                <select className={inp} value={form.business_category} onChange={fld("business_category")} data-testid="profile-business-category">
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Lab>
              <Lab label="Website (optional)"><input className={inp} value={form.website} onChange={fld("website")} placeholder="https://" data-testid="profile-website" /></Lab>
              <Lab label="GST number (optional)"><input className={inp} value={form.gst_number} onChange={fld("gst_number")} data-testid="profile-gst" /></Lab>
              <Lab label="PAN number (optional)"><input className={inp} value={form.pan_number} onChange={fld("pan_number")} data-testid="profile-pan" /></Lab>
            </div>
          </Group>

          <Group title="Contact person">
            <div className="grid md:grid-cols-2 gap-4">
              <Lab label="Contact name *"><input className={inp} value={form.contact_person} onChange={fld("contact_person")} data-testid="profile-contact-person" /></Lab>
              <Lab label="Phone *"><input className={inp} value={form.phone} onChange={fld("phone")} data-testid="profile-phone" /></Lab>
              <Lab label="Email *"><input className={inp} type="email" value={form.email} onChange={fld("email")} data-testid="profile-email" /></Lab>
            </div>
          </Group>

          <Group title="Address">
            <Lab label="Street address *"><input className={inp} value={form.address} onChange={fld("address")} data-testid="profile-address" /></Lab>
            <div className="grid md:grid-cols-4 gap-4">
              <Lab label="City *"><input className={inp} value={form.city} onChange={fld("city")} data-testid="profile-city" /></Lab>
              <Lab label="State *"><input className={inp} value={form.state} onChange={fld("state")} data-testid="profile-state" /></Lab>
              <Lab label="PIN code *"><input className={inp} value={form.pin_code} onChange={fld("pin_code")} data-testid="profile-pin" /></Lab>
              <Lab label="Country *"><input className={inp} value={form.country} onChange={fld("country")} data-testid="profile-country" /></Lab>
            </div>
          </Group>

          <Group title="Special instructions (optional)">
            <textarea className={inp + " min-h-24"} rows={3} value={form.special_instructions}
              onChange={fld("special_instructions")} data-testid="profile-special-instructions"
              placeholder="Brand voice, tone, things to avoid, target audience details, competitors, etc." />
          </Group>

          <div className="flex justify-end pt-4">
            <button onClick={submit} disabled={saving} data-testid="profile-submit"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg brand-gradient text-sm font-semibold hover:opacity-90 disabled:opacity-50">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
              Continue to plan selection
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Lab({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs text-white/40 uppercase tracking-[0.2em]">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Group({ title, children }) {
  return (
    <div>
      <h3 className="font-heading text-sm font-black text-indigo-300 tracking-[0.18em] uppercase mb-3">{title}</h3>
      <div className="space-y-4">{children}</div>
    </div>
  );
}
